(() => {
  'use strict';

  const SELECTORS = {
    drawer: '[data-cart-drawer]',
    menu: '[data-mobile-menu]',
    search: '[data-search-modal]',
    toast: '#ThemeToast'
  };

  class FocusTrap {
    constructor(container) {
      this.container = container;
      this.previous = document.activeElement;
      this.onKeydown = this.onKeydown.bind(this);
      document.addEventListener('keydown', this.onKeydown);
    }
    focusables() {
      return [...this.container.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')]
        .filter((el) => !el.hidden && el.offsetParent !== null);
    }
    onKeydown(event) {
      if (event.key !== 'Tab') return;
      const items = this.focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    destroy() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.previous && document.contains(this.previous)) this.previous.focus();
    }
  }

  class UnibudsTheme {
    constructor() {
      this.drawer = document.querySelector(SELECTORS.drawer);
      this.toast = document.querySelector(SELECTORS.toast);
      this.activeTrap = null;
      this.toastTimer = null;
      this.bindGlobalEvents();
      this.initReveal(document);
      this.initStickyAtc(document);
      this.restoreAnnouncementState();
    }

    bindGlobalEvents() {
      const header = document.querySelector('.site-header');
      if (header) {
        const updateHeader = () => header.classList.toggle('is-scrolled', window.scrollY > 20);
        updateHeader();
        window.addEventListener('scroll', updateHeader, { passive: true });
      }

      document.addEventListener('click', (event) => {
        const menuOpen = event.target.closest('[data-menu-open]');
        const menuClose = event.target.closest('[data-menu-close]');
        const cartOpen = event.target.closest('[data-cart-open]');
        const cartClose = event.target.closest('[data-cart-close]');
        const searchOpen = event.target.closest('[data-search-open]');
        const searchClose = event.target.closest('[data-search-close]');
        const quantityButton = event.target.closest('[data-qty-change]');
        const announcementClose = event.target.closest('[data-announcement-close]');

        if (menuOpen) this.toggleMenu(true);
        if (menuClose) this.toggleMenu(false);
        if (cartOpen) {
          event.preventDefault();
          this.openCart();
        }
        if (cartClose) this.closeCart();
        if (searchOpen) this.toggleSearch(true);
        if (searchClose) this.toggleSearch(false);
        if (quantityButton) this.changeQuantity(quantityButton);
        if (announcementClose) this.dismissAnnouncement(announcementClose);
      });

      document.addEventListener('submit', (event) => {
        const form = event.target;
        if (!form.matches('.js-product-form')) return;
        const submitter = event.submitter;
        if (!submitter || !submitter.matches('[data-add-to-cart]')) return;
        event.preventDefault();
        this.addToCart(form, submitter);
      });

      document.addEventListener('change', (event) => {
        if (event.target.matches('[data-sort-by]')) {
          event.target.form?.submit();
          return;
        }
        if (event.target.matches('[data-option]')) {
          const form = event.target.closest('.product-form');
          if (form) this.updateVariant(form);
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.closeCart();
        this.toggleMenu(false);
        this.toggleSearch(false);
      });

      document.addEventListener('shopify:section:load', (event) => {
        this.initReveal(event.target);
        this.initStickyAtc(event.target);
      });
    }

    setTrap(container, focusSelector) {
      if (this.activeTrap) this.activeTrap.destroy();
      this.activeTrap = container ? new FocusTrap(container) : null;
      if (container) requestAnimationFrame(() => container.querySelector(focusSelector)?.focus());
    }

    clearTrap() {
      if (this.activeTrap) this.activeTrap.destroy();
      this.activeTrap = null;
    }

    toggleMenu(open) {
      const menu = document.querySelector(SELECTORS.menu);
      if (!menu) return;
      if (open) this.toggleSearch(false);
      menu.classList.toggle('is-open', open);
      menu.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('menu-open', open);
      if (open) this.setTrap(menu, '[data-menu-close]');
      else if (menu.classList.contains('is-open') === false) this.clearTrap();
    }

    toggleSearch(open) {
      const modal = document.querySelector(SELECTORS.search);
      if (!modal) return;
      if (open) this.toggleMenu(false);
      modal.classList.toggle('is-open', open);
      modal.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('search-open', open);
      if (open) this.setTrap(modal, 'input[type="search"]');
      else if (!modal.classList.contains('is-open')) this.clearTrap();
    }

    async addToCart(form, button) {
      if (button.disabled) return;
      const label = button.querySelector('[data-button-label]') || button.querySelector('span');
      const defaultLabel = button.dataset.defaultLabel || label?.textContent || 'Add to cart';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Adding…';

      try {
        const data = new FormData(form);
        data.set('sections', 'cart-drawer');
        data.set('sections_url', window.location.pathname);
        const response = await fetch(window.theme.routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: data,
          credentials: 'same-origin'
        });
        const result = await response.json();
        if (!response.ok) throw result;
        await this.updateCartUi(result);
        if (label) label.textContent = 'Added ✓';
        this.openCart();
        this.showToast('Added to your cart');
      } catch (error) {
        this.showToast(error.description || error.message || 'We could not add this item. Please try again.');
      } finally {
        window.setTimeout(() => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          if (label) label.textContent = defaultLabel;
        }, 900);
      }
    }

    async changeQuantity(button) {
      if (button.disabled) return;
      const line = Number(button.dataset.line);
      const delta = Number(button.dataset.qtyChange);
      const input = button.closest('.cart-item')?.querySelector('.quantity input');
      const current = Number(input?.value || 0);
      const quantity = Math.max(0, current + delta);
      button.disabled = true;

      try {
        const response = await fetch(window.theme.routes.cartChange, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            line,
            quantity,
            sections: ['cart-drawer'],
            sections_url: window.location.pathname
          })
        });
        const result = await response.json();
        if (!response.ok) throw result;
        await this.updateCartUi(result);
      } catch (error) {
        button.disabled = false;
        this.showToast(error.description || 'We could not update your cart. Please try again.');
      }
    }

    async updateCartUi(result = {}) {
      let sectionHtml = result.sections?.['cart-drawer'];
      let cart = result.item_count !== undefined ? result : null;

      if (!sectionHtml) {
        const separator = window.theme.routes.root.includes('?') ? '&' : '?';
        const sectionResponse = await fetch(`${window.theme.routes.root}${separator}section_id=cart-drawer`, { credentials: 'same-origin' });
        sectionHtml = await sectionResponse.text();
      }
      this.replaceDrawer(sectionHtml);

      if (!cart) {
        const cartResponse = await fetch(window.theme.routes.cartJson, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        cart = await cartResponse.json();
      }
      this.updateCartCount(cart.item_count || 0);
    }

    replaceDrawer(html) {
      if (!html || !this.drawer) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector(SELECTORS.drawer);
      if (!next) return;
      const wasOpen = this.drawer.classList.contains('is-open');
      this.drawer.innerHTML = next.innerHTML;
      this.drawer.dataset.cartItemCount = next.dataset.cartItemCount || '0';
      this.drawer.setAttribute('aria-hidden', String(!wasOpen));
    }

    updateCartCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((element) => {
        element.textContent = count;
        element.hidden = count === 0;
      });
      document.querySelectorAll('[data-cart-open]').forEach((element) => {
        element.setAttribute('aria-label', `Cart with ${count} item${count === 1 ? '' : 's'}`);
      });
    }

    openCart() {
      this.drawer = document.querySelector(SELECTORS.drawer);
      if (!this.drawer) return;
      this.toggleMenu(false);
      this.toggleSearch(false);
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      this.setTrap(this.drawer, '[data-cart-close]');
    }

    closeCart() {
      this.drawer = document.querySelector(SELECTORS.drawer);
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      this.clearTrap();
    }

    updateVariant(form) {
      const selectedOptions = [...form.querySelectorAll('[data-option]:checked')].map((input) => input.value);
      const json = form.querySelector('[data-variants-json]');
      if (!json) return;
      const variants = JSON.parse(json.textContent);
      const variant = variants.find((item) => item.options.every((option, index) => option === selectedOptions[index]));
      const addButton = form.querySelector('[data-add-to-cart]');
      const addLabel = addButton?.querySelector('[data-button-label]') || addButton?.querySelector('span');

      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
        return;
      }

      form.querySelector('[name="id"]').value = variant.id;
      const productInfo = form.closest('.product-info');
      const currentPrice = productInfo?.querySelector('[data-product-price]');
      const comparePrice = productInfo?.querySelector('[data-compare-price]');
      const saleBadge = productInfo?.querySelector('[data-sale-badge]');
      const stickyPrice = document.querySelector('[data-sticky-price]');
      const formattedPrice = this.money(variant.price);
      const hasSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (currentPrice) currentPrice.textContent = formattedPrice;
      if (stickyPrice) stickyPrice.textContent = formattedPrice;
      if (comparePrice) {
        comparePrice.textContent = hasSale ? this.money(variant.compare_at_price) : '';
        comparePrice.hidden = !hasSale;
      }
      if (saleBadge) {
        const percent = hasSale ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
        saleBadge.textContent = `Save ${percent}%`;
        saleBadge.hidden = !hasSale;
      }
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.dataset.defaultLabel = variant.available ? 'Add to cart' : 'Sold out';
      }
      if (addLabel) addLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';

      const mediaId = variant.featured_media?.id || variant.featured_image?.id;
      if (mediaId) {
        const media = document.querySelector(`[data-media-id="${mediaId}"]`);
        media?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.theme.currency || 'USD'
        }).format(cents / 100);
      } catch (_) {
        return (cents / 100).toFixed(2);
      }
    }

    initReveal(scope) {
      const elements = scope.querySelectorAll?.('.reveal:not([data-reveal-ready])') || [];
      if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        elements.forEach((element) => element.classList.add('is-visible'));
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -30px' });
      elements.forEach((element, index) => {
        element.dataset.revealReady = 'true';
        element.style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
        observer.observe(element);
      });
    }

    initStickyAtc(scope) {
      const main = scope.querySelector?.('[data-main-atc]') || document.querySelector('[data-main-atc]');
      const sticky = scope.querySelector?.('[data-sticky-atc]') || document.querySelector('[data-sticky-atc]');
      if (!main || !sticky || sticky.dataset.ready || !('IntersectionObserver' in window)) return;
      sticky.dataset.ready = 'true';
      const media = window.matchMedia('(max-width: 640px)');
      const observer = new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('is-visible', media.matches && !entry.isIntersecting);
      }, { threshold: 0.1 });
      observer.observe(main);
      sticky.querySelector('button')?.addEventListener('click', () => main.click());
      media.addEventListener?.('change', () => {
        if (!media.matches) sticky.classList.remove('is-visible');
      });
    }

    showToast(message) {
      if (!this.toast) return;
      this.toast.textContent = message;
      this.toast.classList.add('is-visible');
      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => this.toast.classList.remove('is-visible'), 2800);
    }

    dismissAnnouncement(button) {
      button.closest('.announcement')?.remove();
      try { sessionStorage.setItem('unibuds-announcement-dismissed', '1'); } catch (_) {}
    }

    restoreAnnouncementState() {
      try {
        if (sessionStorage.getItem('unibuds-announcement-dismissed') === '1') {
          document.querySelector('.announcement')?.remove();
        }
      } catch (_) {}
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.dataset.loaded) return;
      this.dataset.loaded = 'true';
      fetch(this.dataset.url, { credentials: 'same-origin' })
        .then((response) => response.text())
        .then((text) => {
          const source = new DOMParser().parseFromString(text, 'text/html').querySelector('product-recommendations');
          if (!source?.innerHTML.trim()) return;
          this.innerHTML = source.innerHTML;
          this.querySelectorAll('.reveal').forEach((element) => element.classList.add('is-visible'));
        })
        .catch(() => {});
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  document.addEventListener('DOMContentLoaded', () => new UnibudsTheme());
})();
